/**
 * Etiket tasarım kanvası (Konva).
 *
 * Model mm cinsinden tutulur, Konva px ile çalışır. Dönüşüm tek noktada:
 * `pxPerMm` çarpanı. Render sırasında mm → px, sürükleme/boyutlandırma
 * geri dönüşünde px → mm. Stage'e scale vermek yerine açık çarpım kullanıyoruz;
 * scale'lı stage'de küçük fontSize değerlerinde (3-4) Konva'nın metin ölçümü
 * yuvarlama hatası veriyor.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Stage, Layer, Rect, Text, Image as KonvaImage, Line, Transformer } from 'react-konva';
import Konva from 'konva';
import {
  LabelElement,
  LabelTemplate,
  PlaceholderData,
  getDesignSize,
  isElementVisible,
  mmToPx,
  resolvePlaceholders,
} from '../../../types/labelTemplate.types';
import { getCachedFittedBarcodeImage, getCachedQr } from '../utils/barcodeRenderer';
import { resolveImageSrc } from '../assets/domesticBadge';

export interface LabelCanvasProps {
  template: LabelTemplate;
  onElementsChange: (elements: LabelElement[]) => void;
  selectedId: string | null;
  onSelectElement: (id: string | null) => void;
  /** Önizlemede kullanılacak örnek veri */
  placeholderData?: PlaceholderData;
  /** true: {{token}} ham gösterilir · false: örnek veriyle çözümlenir */
  showRawPlaceholders?: boolean;
  zoom?: number;
  showGrid?: boolean;
  /** Sürüklemeyi bu mm adımına yapıştır (0 = kapalı) */
  snapMm?: number;
  stageRef?: React.RefObject<Konva.Stage | null>;
}

/** Data URL'den Konva'nın kullanabileceği image nesnesi üretir */
function useDataUrlImage(dataUrl: string | null): HTMLImageElement | null {
  const [img, setImg] = useState<HTMLImageElement | null>(null);

  useEffect(() => {
    if (!dataUrl) {
      setImg(null);
      return;
    }
    const image = new window.Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => setImg(image);
    image.onerror = () => setImg(null);
    image.src = dataUrl;
    return () => {
      image.onload = null;
      image.onerror = null;
    };
  }, [dataUrl]);

  return img;
}

/**
 * Görsel hazır değilken gösterilen uyarı çerçevesi.
 *
 * Ayrı bir Rect döndürmek yerine aynı KonvaImage düğümüne uygulanıyor:
 * düğüm tipi değişirse React unmount/remount yapıyor, Transformer ise yok
 * edilmiş düğümü tutmaya devam edip sonraki fare hareketinde çöküyor.
 */
function missingVisual(missing: boolean, fill: string, stroke: string) {
  if (!missing) return {};
  return { fill, stroke, strokeWidth: 1, dash: [4, 3] };
}

interface ElementRenderProps {
  el: LabelElement;
  s: number;
  /** Şablonun yazıcı çözünürlüğü — barkod bu ölçüde üretilir */
  dpi: number;
  resolvedText: string;
  setRef: (node: Konva.Node | null) => void;
  common: Record<string, unknown>;
}

/**
 * Metin kutuya sığmazsa punto küçültür.
 * Ürün adları çok uzun olabiliyor; sabit puntoda etiketten taşıyor.
 */
const CanvasText: React.FC<ElementRenderProps> = ({ el, s, resolvedText, setRef, common }) => {
  const nodeRef = useRef<Konva.Text | null>(null);
  const baseFontPx = (el.fontSizeMm ?? 3) * s;
  const [fontPx, setFontPx] = useState(baseFontPx);

  const boxHeightPx = el.heightMm * s;

  useEffect(() => {
    setFontPx(baseFontPx);
  }, [baseFontPx, resolvedText, el.widthMm, el.heightMm, el.autoShrink]);

  useEffect(() => {
    if (!el.autoShrink) return;
    const node = nodeRef.current;
    if (!node || boxHeightPx <= 0) return;

    const actual = node.height();
    if (actual > boxHeightPx + 0.5 && fontPx > 1) {
      const next = Math.max(1, fontPx * (boxHeightPx / actual) * 0.97);
      if (Math.abs(next - fontPx) > 0.05) setFontPx(next);
    }
  }, [fontPx, boxHeightPx, resolvedText, el.autoShrink]);

  const fontStyle = [el.bold ? 'bold' : '', el.italic ? 'italic' : ''].filter(Boolean).join(' ') || 'normal';

  return (
    <Text
      ref={(node) => {
        nodeRef.current = node;
        setRef(node);
      }}
      x={el.xMm * s}
      y={el.yMm * s}
      width={el.widthMm * s}
      text={resolvedText}
      fontSize={fontPx}
      fontFamily={el.fontFamily ?? 'Arial'}
      fontStyle={fontStyle}
      fill={el.color ?? '#000000'}
      align={el.align ?? 'left'}
      lineHeight={el.lineHeight ?? 1.2}
      wrap="word"
      rotation={el.rotation ?? 0}
      opacity={el.opacity ?? 1}
      {...common}
    />
  );
};

const CanvasBarcode: React.FC<ElementRenderProps> = ({ el, s, dpi, resolvedText, setRef, common }) => {
  const wPx = el.widthMm * s;
  const hPx = el.heightMm * s;

  // Barkod yazıcının dot ölçüsünde üretilir; böylece tasarımda görülen çubuk
  // kalınlıkları ve yanlardaki sessiz bölge baskıdakiyle aynı olur
  const dataUrl = useMemo(
    () =>
      getCachedFittedBarcodeImage(resolvedText, {
        format: el.barcodeFormat ?? 'CODE128',
        boxWidthPx: Math.round((el.widthMm * dpi) / 25.4),
        boxHeightPx: Math.round((el.heightMm * dpi) / 25.4),
        showText: el.showBarcodeText ?? true,
        color: el.color ?? '#000000',
      })?.url ?? null,
    [resolvedText, el.barcodeFormat, el.widthMm, el.heightMm, el.showBarcodeText, el.color, dpi],
  );

  const img = useDataUrlImage(dataUrl);

  return (
    <KonvaImage
      ref={(node) => setRef(node)}
      image={img ?? undefined}
      x={el.xMm * s}
      y={el.yMm * s}
      width={wPx}
      height={hPx}
      {...missingVisual(!img, '#fff4f4', '#d32f2f')}
      rotation={el.rotation ?? 0}
      opacity={el.opacity ?? 1}
      {...common}
    />
  );
};

const CanvasQr: React.FC<ElementRenderProps> = ({ el, s, resolvedText, setRef, common }) => {
  const sizePx = Math.min(el.widthMm, el.heightMm) * s;

  const dataUrl = useMemo(
    () =>
      getCachedQr(resolvedText, {
        size: 320,
        errorLevel: el.qrErrorLevel ?? 'M',
        color: el.color ?? '#000000',
      }),
    [resolvedText, el.qrErrorLevel, el.color],
  );

  const img = useDataUrlImage(dataUrl);

  return (
    <KonvaImage
      ref={(node) => setRef(node)}
      image={img ?? undefined}
      x={el.xMm * s}
      y={el.yMm * s}
      width={sizePx}
      height={sizePx}
      {...missingVisual(!img, '#fff4f4', '#d32f2f')}
      rotation={el.rotation ?? 0}
      opacity={el.opacity ?? 1}
      {...common}
    />
  );
};

const CanvasPicture: React.FC<ElementRenderProps> = ({ el, s, setRef, common }) => {
  const img = useDataUrlImage(resolveImageSrc(el.src) || null);
  const wPx = el.widthMm * s;
  const hPx = el.heightMm * s;

  return (
    <KonvaImage
      ref={(node) => setRef(node)}
      image={img ?? undefined}
      x={el.xMm * s}
      y={el.yMm * s}
      width={wPx}
      height={hPx}
      {...missingVisual(!img, '#f5f5f5', '#bdbdbd')}
      rotation={el.rotation ?? 0}
      opacity={el.opacity ?? 1}
      {...common}
    />
  );
};

const LabelCanvas: React.FC<LabelCanvasProps> = ({
  template,
  onElementsChange,
  selectedId,
  onSelectElement,
  placeholderData,
  showRawPlaceholders = false,
  zoom = 3,
  showGrid = true,
  snapMm = 0,
  stageRef: externalStageRef,
}) => {
  const internalStageRef = useRef<Konva.Stage>(null);
  const stageRef = externalStageRef ?? internalStageRef;
  const transformerRef = useRef<Konva.Transformer>(null);
  const shapeRefs = useRef<Record<string, Konva.Node | null>>({});
  const [editingTextId, setEditingTextId] = useState<string | null>(null);

  const s = mmToPx(1) * zoom;
  const design = getDesignSize(template);
  const stageW = design.widthMm * s;
  const stageH = design.heightMm * s;

  const elements = template.elements;

  const setShapeRef = useCallback((id: string, node: Konva.Node | null) => {
    if (node) {
      shapeRefs.current[id] = node;
      return;
    }
    delete shapeRefs.current[id];

    // Düğüm sahneden çıktıysa Transformer'ı hemen boşalt. Sürükleme
    // sürüyorsa Konva yok edilmiş düğümün parent'ına erişip hata veriyor.
    const tr = transformerRef.current;
    if (tr && tr.nodes().some((n) => !n.getStage())) {
      tr.nodes([]);
      tr.getLayer()?.batchDraw();
    }
  }, []);

  // Transformer'ı seçili elemana bağla
  useEffect(() => {
    const tr = transformerRef.current;
    if (!tr) return;
    const node = selectedId ? shapeRefs.current[selectedId] : null;
    const el = elements.find((e) => e.id === selectedId);
    // getStage() kontrolü: ref hâlâ duruyor olabilir ama düğüm yok edilmiş olabilir
    const attachable = node && node.getStage() && el && !el.locked;
    tr.nodes(attachable ? [node] : []);
    tr.getLayer()?.batchDraw();
  }, [selectedId, elements]);

  const updateElement = useCallback(
    (id: string, attrs: Partial<LabelElement>) => {
      onElementsChange(elements.map((el) => (el.id === id ? { ...el, ...attrs } : el)));
    },
    [elements, onElementsChange],
  );

  const snap = useCallback(
    (mm: number) => (snapMm > 0 ? Math.round(mm / snapMm) * snapMm : Math.round(mm * 100) / 100),
    [snapMm],
  );

  // Delete ve ok tuşlarıyla ince konumlandırma
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!selectedId || editingTextId) return;
      const target = e.target as HTMLElement | null;
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;

      const el = elements.find((x) => x.id === selectedId);
      if (!el) return;

      if (e.key === 'Delete') {
        onElementsChange(elements.filter((x) => x.id !== selectedId));
        onSelectElement(null);
        return;
      }

      const step = e.shiftKey ? 1 : 0.2;
      const moves: Record<string, [number, number]> = {
        ArrowLeft: [-step, 0],
        ArrowRight: [step, 0],
        ArrowUp: [0, -step],
        ArrowDown: [0, step],
      };
      const move = moves[e.key];
      if (move && !el.locked) {
        e.preventDefault();
        updateElement(selectedId, {
          xMm: Math.round((el.xMm + move[0]) * 100) / 100,
          yMm: Math.round((el.yMm + move[1]) * 100) / 100,
        });
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedId, elements, editingTextId, onElementsChange, onSelectElement, updateElement]);

  const handleStageClick = (e: Konva.KonvaEventObject<MouseEvent>) => {
    if (e.target === e.target.getStage() || e.target.attrs?.id === '__bg') {
      onSelectElement(null);
    }
  };

  const handleDragEnd = (id: string) => (e: Konva.KonvaEventObject<DragEvent>) => {
    updateElement(id, {
      xMm: snap(e.target.x() / s),
      yMm: snap(e.target.y() / s),
    });
  };

  const handleTransformEnd = (id: string) => () => {
    const node = shapeRefs.current[id];
    if (!node) return;
    const scaleX = node.scaleX();
    const scaleY = node.scaleY();
    node.scaleX(1);
    node.scaleY(1);

    const el = elements.find((x) => x.id === id);
    const newWidthMm = Math.max(0.5, (node.width() * scaleX) / s);
    const newHeightMm = Math.max(0.2, (node.height() * scaleY) / s);

    const attrs: Partial<LabelElement> = {
      xMm: snap(node.x() / s),
      yMm: snap(node.y() / s),
      widthMm: Math.round(newWidthMm * 100) / 100,
      rotation: Math.round(node.rotation() * 10) / 10,
    };

    // Konva Text yüksekliği içerikten türetir; kullanıcının çektiği yüksekliği
    // korumak için metinde height'ı ayrıca yazıyoruz
    if (el?.type === 'text') {
      attrs.heightMm = Math.round(Math.max(1, ((el.heightMm ?? 5) * scaleY)) * 100) / 100;
    } else {
      attrs.heightMm = Math.round(newHeightMm * 100) / 100;
    }

    updateElement(id, attrs);
  };

  const handleTextDblClick = (el: LabelElement) => {
    const stage = stageRef.current;
    if (!stage || el.locked) return;

    setEditingTextId(el.id);
    onSelectElement(null);

    const container = stage.container();
    const stageBox = container.getBoundingClientRect();
    const node = shapeRefs.current[el.id];
    if (!node) {
      setEditingTextId(null);
      return;
    }

    const pos = node.getAbsolutePosition();
    const textarea = document.createElement('textarea');
    document.body.appendChild(textarea);

    // Düzenlemede HAM metin gösterilir; kullanıcı {{urunAdi}} yazabilsin
    textarea.value = el.text ?? '';
    textarea.style.position = 'absolute';
    textarea.style.top = `${stageBox.top + pos.y + window.scrollY}px`;
    textarea.style.left = `${stageBox.left + pos.x + window.scrollX}px`;
    textarea.style.width = `${Math.max(120, el.widthMm * s)}px`;
    textarea.style.minHeight = `${Math.max(28, el.heightMm * s)}px`;
    textarea.style.fontSize = `${Math.max(12, (el.fontSizeMm ?? 3) * s)}px`;
    textarea.style.fontFamily = el.fontFamily ?? 'Arial';
    textarea.style.border = '2px solid #1976d2';
    textarea.style.borderRadius = '2px';
    textarea.style.padding = '2px 4px';
    textarea.style.margin = '0';
    textarea.style.overflow = 'hidden';
    textarea.style.background = 'rgba(255,255,255,0.98)';
    textarea.style.outline = 'none';
    textarea.style.resize = 'none';
    textarea.style.lineHeight = String(el.lineHeight ?? 1.2);
    textarea.style.color = el.color ?? '#000';
    textarea.style.zIndex = '2000';
    textarea.focus();
    textarea.select();

    const finish = () => {
      const newText = textarea.value;
      textarea.removeEventListener('blur', finish);
      textarea.remove();
      updateElement(el.id, { text: newText });
      setEditingTextId(null);
    };

    textarea.addEventListener('blur', finish);
    textarea.addEventListener('keydown', (ev) => {
      if (ev.key === 'Escape') textarea.blur();
      if (ev.key === 'Enter' && !ev.shiftKey) {
        ev.preventDefault();
        textarea.blur();
      }
    });
  };

  const gridLines = useMemo(() => {
    if (!showGrid) return [];
    const lines: React.ReactElement[] = [];
    const stepMm = 5;
    for (let x = stepMm; x < design.widthMm; x += stepMm) {
      lines.push(
        <Line
          key={`gx-${x}`}
          points={[x * s, 0, x * s, stageH]}
          stroke="#e3f2fd"
          strokeWidth={1}
          listening={false}
        />,
      );
    }
    for (let y = stepMm; y < design.heightMm; y += stepMm) {
      lines.push(
        <Line
          key={`gy-${y}`}
          points={[0, y * s, stageW, y * s]}
          stroke="#e3f2fd"
          strokeWidth={1}
          listening={false}
        />,
      );
    }
    return lines;
  }, [showGrid, design.widthMm, design.heightMm, s, stageW, stageH]);

  const renderElement = (el: LabelElement) => {
    // Koşulu sağlanmayan eleman baskıda çıkmayacak. Tasarımcıda gizlenmiyor —
    // gizlense seçilemez ve düzenlenemez olurdu; soluk gösterilip uyarılıyor.
    const conditionMet = isElementVisible(el, placeholderData);

    const common = {
      draggable: !el.locked,
      onDragEnd: handleDragEnd(el.id),
      onTransformEnd: handleTransformEnd(el.id),
      onClick: () => onSelectElement(el.id),
      onTap: () => onSelectElement(el.id),
      // Elemanlar kendi opacity'sini yazıyor ama {...common} sonra geldiği için
      // buradaki değer kazanır; yalnızca koşul sağlanmadığında devreye giriyor
      ...(conditionMet ? {} : { opacity: (el.opacity ?? 1) * 0.25 }),
    };

    const setRef = (node: Konva.Node | null) => setShapeRef(el.id, node);

    const rawText = el.type === 'barcode' ? el.barcodeValue ?? '' : el.type === 'qr' ? el.qrValue ?? '' : el.text ?? '';
    // Barkod/QR her zaman çözümlenir; ham {{token}} okunabilir barkod üretmez
    const alwaysResolve = el.type === 'barcode' || el.type === 'qr';
    const resolvedText =
      showRawPlaceholders && !alwaysResolve ? rawText : resolvePlaceholders(rawText, placeholderData);

    const props: ElementRenderProps = { el, s, dpi: template.dpi, resolvedText, setRef, common };

    switch (el.type) {
      case 'text':
        return (
          <CanvasText
            key={el.id}
            {...props}
            common={{
              ...common,
              visible: editingTextId !== el.id,
              onDblClick: () => handleTextDblClick(el),
              onDblTap: () => handleTextDblClick(el),
            }}
          />
        );
      case 'barcode':
        return <CanvasBarcode key={el.id} {...props} />;
      case 'qr':
        return <CanvasQr key={el.id} {...props} />;
      case 'image':
        return <CanvasPicture key={el.id} {...props} />;
      case 'rect':
        return (
          <Rect
            key={el.id}
            ref={setRef}
            x={el.xMm * s}
            y={el.yMm * s}
            width={el.widthMm * s}
            height={el.heightMm * s}
            fill={el.fillColor === 'transparent' ? undefined : el.fillColor ?? undefined}
            stroke={el.strokeColor ?? '#000000'}
            strokeWidth={(el.strokeWidthMm ?? 0.3) * s}
            cornerRadius={(el.cornerRadiusMm ?? 0) * s}
            rotation={el.rotation ?? 0}
            opacity={el.opacity ?? 1}
            {...common}
          />
        );
      case 'line':
        return (
          <Rect
            key={el.id}
            ref={setRef}
            x={el.xMm * s}
            y={el.yMm * s}
            width={el.widthMm * s}
            height={Math.max(1, el.heightMm * s)}
            fill={el.fillColor ?? '#000000'}
            rotation={el.rotation ?? 0}
            opacity={el.opacity ?? 1}
            {...common}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div
      style={{
        width: stageW,
        height: stageH,
        position: 'relative',
        background: '#fff',
        boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
        // Etiketin fiziksel kenarını göster
        outline: '1px solid #90a4ae',
      }}
    >
      <Stage
        ref={stageRef}
        width={stageW}
        height={stageH}
        onClick={handleStageClick}
        onTap={handleStageClick}
      >
        <Layer listening={false}>
          <Rect
            id="__bg"
            x={0}
            y={0}
            width={stageW}
            height={stageH}
            fill={template.backgroundColor ?? '#ffffff'}
          />
          {gridLines}
        </Layer>
        <Layer>
          {elements.map(renderElement)}
          <Transformer
            ref={transformerRef}
            rotateEnabled
            anchorSize={8}
            borderStroke="#1976d2"
            anchorStroke="#1976d2"
            enabledAnchors={[
              'top-left',
              'top-right',
              'bottom-left',
              'bottom-right',
              'middle-left',
              'middle-right',
              'top-center',
              'bottom-center',
            ]}
            boundBoxFunc={(oldBox, newBox) => (newBox.width < 4 || newBox.height < 2 ? oldBox : newBox)}
          />
        </Layer>
      </Stage>
    </div>
  );
};

export default LabelCanvas;
